import { useCallback, useRef, useState } from "react";
import LoadingSpinner from "@imminently/decisively-ui/LoadingSpinner";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import debounce from "lodash/debounce";
import Button from "@imminently/decisively-ui/Button";
import DropdownMenu from "@imminently/decisively-ui/DropdownMenu";
import Dialog, { useDialogContext } from "@imminently/decisively-ui/Dialog";
import { DialogHeader } from "@/ui/dialogs/DialogHeader";
import EmptyStatus from "@imminently/decisively-ui/EmptyStatus";
import TextInput from "@imminently/decisively-ui/TextInput";
import { useConfirmDialog } from "@imminently/decisively-ui/ConfirmDialog";
import { workspaceService } from "@/app/providers/services";
import { useNotify } from "@/common/notifications";
import { useScrollable } from "@/hooks/use-scrollable";
import { setWorkspace, useScope } from "@/redux/scope";
import { WORKSPACE_LAYER } from "@/ui/dialogs/layers";

import type { BackendResource } from "@imminently/immi-query";
import type { Workspace } from "@packages/commons";
import { WorkspaceCard } from "@/ui/blocks/cards/WorkspaceCard";
import { CardSkeleton } from "@/ui/blocks/cards/CardSkeleton";
import type { CardUser } from "@/ui/blocks/cards/_shared";
import { useAddWorkspaceModal, useEditWorkspaceModal } from "../hooks/useWorkspaceModal";
import SectionLabel from "@imminently/decisively-ui/SectionLabel";

// ─── Search ──────────────────────────────────────────────────────────────────

const useWorkspaceSearch = () => {
  const [value, setValue] = useState("");
  const [filter, setFilter] = useState("");

  const debouncedSet = useRef(debounce(setFilter, 200)).current;

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      setValue(raw);
      debouncedSet(raw.trim().length > 0 ? `*${raw.trim()}*` : "");
    },
    [debouncedSet],
  );

  return { value, onChange, filter };
};

// ─── Delete ───────────────────────────────────────────────────────────────────

const useDeleteWorkspace = () => {
  const dispatch = useDispatch();
  const notify = useNotify();
  const { mutateAsync } = workspaceService.useDelete();
  const { pushConfirmDialog } = useConfirmDialog();

  return useCallback(
    (id: string) => {
      pushConfirmDialog({
        layer: WORKSPACE_LAYER,
        title: "Delete workspace",
        description: "Delete this workspace and all associated projects?",
        confirmLabel: "Yes, delete",
        onConfirm: async () => {
          try {
            await mutateAsync(id);
            notify.info("Workspace deleted");
            dispatch(setWorkspace(null));
          } catch {
            notify.error("Failed to delete workspace");
          }
        },
      });
    },
    [dispatch, mutateAsync, notify, pushConfirmDialog],
  );
};

// ─── Menu ─────────────────────────────────────────────────────────────────────

const WorkspaceMenu = ({ workspace }: { workspace: BackendResource<Workspace> }) => {
  const onDelete = useDeleteWorkspace();
  const openEdit = useEditWorkspaceModal();

  return (
    <>
      <DropdownMenu.Item onClick={() => openEdit(workspace as Workspace)}>
        <DropdownMenu.Item.Label>Edit</DropdownMenu.Item.Label>
      </DropdownMenu.Item>
      <DropdownMenu.Item
        color="destructive"
        onClick={() => onDelete(workspace.id)}
      >
        <DropdownMenu.Item.Label>Delete</DropdownMenu.Item.Label>
      </DropdownMenu.Item>
    </>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toCardUsers = (users: any[]): CardUser[] =>
  (users ?? []).map((u, i) => ({
    id: u.identity_id ?? u.id ?? String(i),
    name: u.display_name ?? u.name ?? u.email ?? "",
  }));

// ─── Modal content ─────────────────────────────────────────────────────────────

interface BrowseWorkspacesModalProps {
  close: () => void;
}

export const BrowseWorkspacesModal = ({ close }: BrowseWorkspacesModalProps) => {
  const { t } = useTranslation();
  const dialogContext = useDialogContext();
  const dispatch = useDispatch();
  const { workspace: activeWorkspaceId } = useScope();
  const search = useWorkspaceSearch();
  const openAdd = useAddWorkspaceModal();

  const {
    data: workspaces,
    total,
    query,
    loadMore,
  } = workspaceService.useInfiniteResources({
    perPage: 12,
    filter: {
      name: search.filter.length > 0 ? search.filter : undefined,
    },
    refetchOnWindowFocus: false,
  });

  const canLoad = !query.isError && !query.isFetching && (query.hasNextPage ?? false);
  const scrollProps = useScrollable(loadMore, canLoad);

  const isLoading = query.isLoading;
  const isEmpty = !isLoading && total === 0;

  const openWorkspace = (id: string) => {
    dispatch(setWorkspace(id));
    dialogContext?.close();
  };

  return (
    <Dialog.Content>
      <DialogHeader title={t("workspaces.title")} description={t("workspaces.create_description")} />

      <Dialog.Body asChild>
        <div className="overflow-hidden p-0 gap-0">
          <div className="flex items-center justify-between gap-3 px-6 py-2">
            <TextInput
              type="search"
              className="flex-1 w-sm"
              placeholder={`${t("search")}...`}
              value={search.value}
              onChange={search.onChange}
            />
            <Button
              label={t("workspaces.create")}
              type="button"
              onClick={openAdd}
            >
              {t("workspaces.create")}
            </Button>
          </div>

          {/* Grid */}
          <div
            className="flex-1 scrollable px-6 pt-2 pb-4"
            {...scrollProps}
          >
            {/* Count + fetch indicator */}
            {!isLoading && (
              <SectionLabel title={t("workspaces.title")} resourceCount={total} className="flex items-center gap-2 pb-4">
                {query.isFetching && <LoadingSpinner size="xs" />}
              </SectionLabel>
            )}
            {isEmpty ? (
              <EmptyStatus>
                <EmptyStatus.Title>
                  {search.filter.trim().length > 0 ? t("workspaces.no_match") : t("workspaces.empty")}
                </EmptyStatus.Title>
              </EmptyStatus>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {isLoading
                  ? Array.from({ length: 6 }, (_, i) => <CardSkeleton key={i} />)
                  : workspaces.map((w) => (
                      <WorkspaceCard
                        key={w.id}
                        name={w.name ?? ""}
                        visibility={w.visibility as "public" | "private"}
                        tags={w.id === activeWorkspaceId ? [t("current")] : []}
                        users={toCardUsers(w.usersData ?? [])}
                        menu={<WorkspaceMenu workspace={w} />}
                        action={{ name: t("workspaces.open"), onClick: () => openWorkspace(w.id) }}
                      />
                    ))}
                {query.isFetchingNextPage && Array.from({ length: 3 }, (_, i) => <CardSkeleton key={`more-${i}`} />)}
              </div>
            )}
          </div>
        </div>
      </Dialog.Body>
    </Dialog.Content>
  );
};
